/**
 * Recurring series (e.g. "Burger Monday") — Learn-Mode inline tips.
 *
 * Anchored across `routes/social/Series.tsx`, `components/series/*` and the "Recurring series" link
 * card on `routes/social/PostingSchedule.tsx`.
 */

import type { LearnTip } from "./types";

export const seriesTips: LearnTip[] = [
  {
    key: "social-series",
    title: "Recurring series",
    body: "A feature that posts on a schedule, like a different burger every Monday. Each week you add the photo and the post is written for you.",
  },
  {
    key: "social-series-list",
    title: "Your series",
    body: "Each card shows when the series posts, where, and the next episode. Pause stops new episodes for a while; End stops it for good. Choose a card to see its next episodes.",
  },
  {
    key: "social-series-form",
    title: "Series settings",
    body: "Set the day, time and networks once. Every week an episode appears and asks for a photo ahead of time.",
  },
  {
    key: "social-series-schedule",
    title: "When it posts",
    body: "Pick the weekday and time in the client's timezone; the zone is shown next to the time. Every N weeks makes it fortnightly or monthly-ish. Ask for the photo this many days ahead.",
  },
  {
    key: "social-series-platforms",
    title: "Networks",
    body: "Where each episode is posted. TikTok and YouTube aren't available for series yet.",
  },
  {
    key: "social-series-template",
    title: "Caption template",
    body: "The caption every episode starts from. Placeholders in curly braces are filled in for each episode.",
  },
  {
    key: "social-series-placeholders",
    title: "Placeholders",
    body: "Click a chip to insert it where the cursor is.",
    bullets: [
      "{item}: what is featured this week",
      "{price}: the price you enter with the photo",
      "{day}: the weekday, e.g. Monday",
      "{hook} and {cta}: lines rotated from your lists",
    ],
  },
  {
    key: "social-series-hooks",
    title: "Hooks",
    body: "Opening lines. Each episode uses the next one in the list, so the series doesn't open the same way every week.",
  },
  {
    key: "social-series-ctas",
    title: "Calls to action",
    body: "Closing lines such as “Come hungry.” Used in rotation, like hooks.",
  },
  {
    key: "social-series-hashtags",
    title: "Hashtag sets",
    body: "Each episode uses one set, in rotation. A set holds at most 5 tags because Instagram caps hashtags at 5.",
  },
  {
    key: "social-series-behavior",
    title: "Variety and approval",
    body: "A photo can come back only after the number of episodes you choose. Turn on the Instagram Story to post one with each episode. Skipping the approval queue still needs a photo first.",
  },
  {
    key: "social-series-preview",
    title: "Preview caption",
    body: "Shows the caption the saved settings would write for a sample item. Save your edits first to see them in the preview.",
  },
  {
    key: "social-series-timeline",
    title: "Next episodes",
    body: "The coming episodes in order. Dashed ones are projected: they become real episodes closer to the date. Attach media gives an episode its photo; Skip drops it with a reason.",
  },
  {
    key: "social-series-attach",
    title: "Attach media",
    body: "Pick the photo from the client's library, add what's featured and the price, and the post is created for approval or scheduling.",
  },
  {
    key: "social-series-variety",
    title: "Variety check",
    body: "Checks the photo and caption against recent episodes. A warning is a heads-up; a block means the photo is too close to a recent one, so pick another.",
  },
  {
    key: "social-series-skip",
    title: "Skip an episode",
    body: "Use this when a week shouldn't post, like a holiday closure. A short reason is required and stays on the episode.",
  },
  {
    key: "social-queue-series",
    title: "Recurring series",
    body: "Weekly features such as Burger Monday live on their own page, with a photo request ahead of each episode.",
  },
];
